<script setup lang="ts">
type TooltipPlacement = "top" | "bottom";
type TooltipAlign = "start" | "center" | "end";

const {
  text,
  placement = "top",
  align = "center",
} = defineProps<{
  text: string;
  placement?: TooltipPlacement;
  align?: TooltipAlign;
}>();

const isOpen = ref(false);
const tooltipId = useId();

const alignClass = computed(() => {
  if (align === "start") return "left-0";
  if (align === "end") return "right-0";
  return "left-1/2 -translate-x-1/2";
});

const arrowAlignClass = computed(() => {
  if (align === "start") return "left-4";
  if (align === "end") return "right-3";
  return "left-1/2 -translate-x-1/2";
});

function open() {
  if (text) isOpen.value = true;
}

function close() {
  isOpen.value = false;
}
</script>

<template>
  <span
    class="relative inline-flex"
    tabindex="0"
    :aria-describedby="isOpen ? tooltipId : undefined"
    @mouseenter="open"
    @mouseleave="close"
    @focusin="open"
    @focusout="close"
    @keydown.esc="close"
  >
    <slot />

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <span
        v-if="isOpen"
        :id="tooltipId"
        role="tooltip"
        class="pointer-events-none absolute z-50 w-max max-w-[230px] whitespace-normal rounded-lg bg-app-text px-3 py-2 text-xs font-medium leading-[1.45] text-white shadow-[0_8px_24px_rgba(17,30,25,0.12)]"
        :class="[
          placement === 'top' ? 'bottom-full mb-2.5' : 'top-full mt-2.5',
          alignClass,
        ]"
      >
        {{ text }}

        <span
          class="absolute size-2.5 rotate-45 bg-app-text"
          :class="[
            placement === 'top' ? '-bottom-1' : '-top-1',
            arrowAlignClass,
          ]"
        />
      </span>
    </Transition>
  </span>
</template>
